import { DEFAULT_SETTINGS } from '../constants/invoice'
import type { Settings, BankAccountData, PaypalData, MollieData, StripeData } from '@dutchinvoice/types'
import { DEFAULT_PAYMENT_METHODS, PaymentMethodType } from '@dutchinvoice/types'
import { PAYMENT_METHOD_TYPES } from './paymentMethodUtils'

/**
 * SettingsManager provides a centralized way to manage settings
 * This makes it easier to add new settings in the future
 */
export class SettingsManager {
    /**
     * Merges new settings with existing settings, ensuring all required fields are present
     * @param currentSettings The current settings object
     * @param newSettings The new settings to merge
     * @returns The merged settings object
     */
    static mergeSettings(currentSettings: Settings, newSettings: Partial<Settings>): Settings {
        // Create a deep copy of the current settings to avoid mutation
        const baseSettings = JSON.parse(JSON.stringify(currentSettings));

        // Start with a merged top-level settings
        const mergedSettings: Settings = {
            ...baseSettings,
            ...newSettings,
            // Add default values if not present
            invoiceStartNumber: newSettings.invoiceStartNumber ?? baseSettings.invoiceStartNumber ?? DEFAULT_SETTINGS.invoiceStartNumber,
            // latestInvoiceNumber should never decrease, always take the highest value
            latestInvoiceNumber: Math.max(
                baseSettings.latestInvoiceNumber || 0,
                newSettings.latestInvoiceNumber || 0
            ),
        };

        // Handle logo explicitly - if newSettings has logo explicitly set to undefined, remove it
        if ('logo' in newSettings) {
            mergedSettings.logo = newSettings.logo;
        }

        // Handle nested objects separately to ensure complete structure
        mergedSettings.invoiceNumberFormat = {
            ...baseSettings.invoiceNumberFormat,
            ...(newSettings.invoiceNumberFormat || {}),
            // Default values for invoiceNumberFormat
            prefix: newSettings.invoiceNumberFormat?.prefix ?? baseSettings.invoiceNumberFormat?.prefix ?? DEFAULT_SETTINGS.invoiceNumberFormat.prefix,
            includeYear: newSettings.invoiceNumberFormat?.includeYear ?? baseSettings.invoiceNumberFormat?.includeYear ?? DEFAULT_SETTINGS.invoiceNumberFormat.includeYear,
            yearFormat: newSettings.invoiceNumberFormat?.yearFormat ?? baseSettings.invoiceNumberFormat?.yearFormat ?? DEFAULT_SETTINGS.invoiceNumberFormat.yearFormat,
            separator: newSettings.invoiceNumberFormat?.separator ?? baseSettings.invoiceNumberFormat?.separator ?? DEFAULT_SETTINGS.invoiceNumberFormat.separator,
            numberPadding: newSettings.invoiceNumberFormat?.numberPadding ?? baseSettings.invoiceNumberFormat?.numberPadding ?? DEFAULT_SETTINGS.invoiceNumberFormat.numberPadding
        };

        // Ensure defaultCompany is present with all required fields
        mergedSettings.defaultCompany = {
            company: '',
            address: '',
            postalCode: '',
            city: '',
            vatNumber: '',
            kvkNumber: '',
            ...(baseSettings.defaultCompany || {}),
            ...(newSettings.defaultCompany || {})
        };

        // Initialize payment methods if not present
        if (!mergedSettings.paymentMethods) {
            mergedSettings.paymentMethods = {};
        }

        // Handle payment methods
        for (const type of PAYMENT_METHOD_TYPES) {
            // Get default data for this payment method type
            const defaultData = DEFAULT_PAYMENT_METHODS[type as keyof typeof DEFAULT_PAYMENT_METHODS];

            // Skip if no default data is available
            if (!defaultData) continue;

            // Get existing data from base and new settings
            const baseData = baseSettings.paymentMethods?.[type as keyof typeof baseSettings.paymentMethods];
            const newData = newSettings.paymentMethods?.[type as keyof typeof newSettings.paymentMethods];

            // Merge the data in the correct order: defaults, then base settings, then new settings
            mergedSettings.paymentMethods[type as keyof typeof mergedSettings.paymentMethods] = {
                ...defaultData,
                ...(baseData || {}),
                ...(newData || {})
            };
        }

        // Handle defaultPaymentMethods
        if (newSettings.defaultPaymentMethods) {
            mergedSettings.defaultPaymentMethods = newSettings.defaultPaymentMethods;
        } else if (!mergedSettings.defaultPaymentMethods) {
            mergedSettings.defaultPaymentMethods = [];
        }

        return mergedSettings;
    }

    /**
     * Validates settings to ensure they meet the required format
     * @param settings The settings to validate
     * @returns The validated settings
     */
    static validateSettings(settings: Partial<Settings>): Partial<Settings> {
        // Create a copy to avoid mutation
        const validatedSettings = { ...settings };

        // Validate country
        if (validatedSettings.country && !['NL', 'BE', 'DE', 'FR'].includes(validatedSettings.country)) {
            validatedSettings.country = DEFAULT_SETTINGS.country;
        }

        // Validate invoice numbers
        if (validatedSettings.invoiceStartNumber && (typeof validatedSettings.invoiceStartNumber !== 'number' || validatedSettings.invoiceStartNumber < 1)) {
            validatedSettings.invoiceStartNumber = DEFAULT_SETTINGS.invoiceStartNumber;
        }

        if (validatedSettings.latestInvoiceNumber && (typeof validatedSettings.latestInvoiceNumber !== 'number' || validatedSettings.latestInvoiceNumber < 1)) {
            validatedSettings.latestInvoiceNumber = DEFAULT_SETTINGS.latestInvoiceNumber;
        }

        // Validate defaultNotes
        if (validatedSettings.defaultNotes && (typeof validatedSettings.defaultNotes !== 'string' || validatedSettings.defaultNotes.length > 500)) {
            validatedSettings.defaultNotes = DEFAULT_SETTINGS.defaultNotes;
        }

        // Validate logo if present or explicitly undefined
        if ('logo' in validatedSettings) {
            // If logo is explicitly set to undefined or null, keep it that way to ensure removal
            if (validatedSettings.logo === undefined || validatedSettings.logo === null) {
                validatedSettings.logo = undefined;
            }
            // No further validation needed - TypeScript and backend will ensure correct structure
        }

        // Validate payment methods
        if (validatedSettings.paymentMethods) {
            for (const type of PAYMENT_METHOD_TYPES) {
                const methodKey = type as keyof typeof validatedSettings.paymentMethods;
                const data = validatedSettings.paymentMethods[methodKey];

                // Basic validation - ensure required fields exist
                if (data) {
                    let isValid = true;

                    switch (type) {
                        case 'bank': {
                            const bankData = data as BankAccountData;
                            if (!bankData.iban || !bankData.accountName) isValid = false;
                            break;
                        }
                        case 'paypal': {
                            const paypalData = data as PaypalData;
                            if (!paypalData.email) isValid = false;
                            break;
                        }
                        case 'mollie': {
                            const mollieData = data as MollieData;
                            if (!mollieData.apiKey) isValid = false;
                            break;
                        }
                        case 'stripe': {
                            const stripeData = data as StripeData;
                            if (!stripeData.accountId) isValid = false;
                            break;
                        }
                    }

                    // If validation fails, use default data
                    if (!isValid) {
                        const defaultData = DEFAULT_PAYMENT_METHODS[methodKey];
                        if (defaultData) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            validatedSettings.paymentMethods[methodKey] = { ...defaultData } as any;
                        }
                    }
                }
            }
        }

        // Validate defaultPaymentMethods
        if (validatedSettings.defaultPaymentMethods) {
            validatedSettings.defaultPaymentMethods = validatedSettings.defaultPaymentMethods.filter(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                type => PAYMENT_METHOD_TYPES.includes(type as any) || type === 'none'
            ) as PaymentMethodType[];
        }

        return validatedSettings;
    }
} 